import { equals } from '../../Equal/dist/effect-Equal.esm.js';
import { dual } from '../../Function/dist/effect-Function.esm.js';
import { toString, toJSON, NodeInspectSymbol } from '../../Inspectable/dist/effect-Inspectable.esm.js';
import { pipeArguments } from '../../Pipeable/dist/effect-Pipeable.esm.js';

/**
 * @since 2.0.0
 */
const TypeId = /*#__PURE__*/Symbol.for("effect/MutableRef");

/**
 * @since 2.0.0
 * @category symbol
 */

/**
 * @since 2.0.0
 * @category models
 */

const MutableRefProto = {
  [TypeId]: TypeId,
  toString() {
    return toString(this.toJSON());
  },
  toJSON() {
    return {
      _id: "MutableRef",
      current: toJSON(this.current)
    };
  },
  [NodeInspectSymbol]() {
    return this.toJSON();
  },
  pipe() {
    return pipeArguments(this, arguments);
  }
};

/**
 * @since 2.0.0
 * @category constructors
 */
const make = value => {
  const ref = Object.create(MutableRefProto);
  ref.current = value;
  return ref;
};

/**
 * @since 2.0.0
 * @category general
 */
const compareAndSet = /*#__PURE__*/dual(3, (self, oldValue, newValue) => {
  if (equals(oldValue, self.current)) {
    self.current = newValue;
    return true;
  }
  return false;
});

/**
 * @since 2.0.0
 * @category numeric
 */
const decrement = self => update(self, n => n - 1);

/**
 * @since 2.0.0
 * @category numeric
 */
const decrementAndGet = self => updateAndGet(self, n => n - 1);

/**
 * @since 2.0.0
 * @category general
 */
const get = self => self.current;

/**
 * @since 2.0.0
 * @category numeric
 */
const getAndDecrement = self => getAndUpdate(self, n => n - 1);

/**
 * @since 2.0.0
 * @category numeric
 */
const getAndIncrement = self => getAndUpdate(self, n => n + 1);

/**
 * @since 2.0.0
 * @category general
 */
const getAndSet = /*#__PURE__*/dual(2, (self, value) => {
  const ret = self.current;
  self.current = value;
  return ret;
});

/**
 * @since 2.0.0
 * @category general
 */
const getAndUpdate = /*#__PURE__*/dual(2, (self, f) => getAndSet(self, f(get(self))));

/**
 * @since 2.0.0
 * @category numeric
 */
const increment = self => update(self, n => n + 1);

/**
 * @since 2.0.0
 * @category numeric
 */
const incrementAndGet = self => updateAndGet(self, n => n + 1);

/**
 * @since 2.0.0
 * @category general
 */
const set = /*#__PURE__*/dual(2, (self, value) => {
  self.current = value;
  return self;
});

/**
 * @since 2.0.0
 * @category general
 */
const setAndGet = /*#__PURE__*/dual(2, (self, value) => {
  self.current = value;
  return self.current;
});

/**
 * @since 2.0.0
 * @category general
 */
const update = /*#__PURE__*/dual(2, (self, f) => set(self, f(get(self))));

/**
 * @since 2.0.0
 * @category general
 */
const updateAndGet = /*#__PURE__*/dual(2, (self, f) => setAndGet(self, f(get(self))));

/**
 * @since 2.0.0
 * @category boolean
 */
const toggle = self => update(self, _ => !_);

export { compareAndSet, decrement, decrementAndGet, get, getAndDecrement, getAndIncrement, getAndSet, getAndUpdate, increment, incrementAndGet, make, set, setAndGet, toggle, update, updateAndGet };
